import { z } from 'zod'
import { normalizeCurrencyCode, roundQuotedPrice } from './currency'

export * from './copy'

export const getLocale = (locale = 'en') => {
  let newLocale = locale.split('-')[0]

  if (newLocale === 'se') newLocale = 'sv'
  if (newLocale === 'nn') newLocale = 'no'

  switch (newLocale) {
    case 'en':
      newLocale = 'en-GB'
      break
    case 'da':
      newLocale = 'da-DK'
      break
    case 'es':
      newLocale = 'es-ES'
      break
    case 'fr':
      newLocale = 'fr-FR'
      break
    case 'no':
      newLocale = 'no-NO'
      break
    case 'sv':
      newLocale = 'sv-SE'
      break
  }

  try {
    return z
      .enum(['en-GB', 'da-DK', 'es-ES', 'fr-FR', 'no-NO', 'sv-SE'])
      .parse(newLocale)
  } catch (error) {
    return 'en-GB'
  }
}

export const getStripeLocale = (locale?: string) => {
  const newLocale = getLocale(locale)

  switch (newLocale) {
    case 'da-DK':
      return 'da'
    case 'no-NO':
      return 'no'
    case 'sv-SE':
      return 'sv'
  }

  return newLocale
}

export const formatDate = (
  date: string | Date,
  options?: Intl.DateTimeFormatOptions,
): string => {
  const { $i18n } = useNuxtApp()

  const dateObject = typeof date === 'string' ? new Date(date) : date

  if (dateObject === null) {
    return 'TBD'
  }
  return dateObject.toLocaleString(getLocale($i18n.locale.value), {
    timeZone: 'UTC',
    dateStyle: 'medium',
    ...options,
  })
}

export function randomString(length = 10) {
  return Math.random().toString(36).slice(2, 2 + length);
}

export const formatTime = (
  date: string | Date,
  options?: Intl.DateTimeFormatOptions,
): string => {
  const { $i18n } = useNuxtApp()

  const dateObject = typeof date === 'string' ? new Date(date) : date

  return dateObject.toLocaleString(getLocale($i18n.locale.value), {
    timeStyle: 'short',
    ...options,
  })
}

export const formatDateTime = (
  date: string | Date,
  options?: Intl.DateTimeFormatOptions,
): string => {
  const { $i18n } = useNuxtApp()

  const dateObject = typeof date === 'string' ? new Date(date) : date

  return dateObject.toLocaleString(getLocale($i18n.locale.value), {
    timeZone: 'UTC',
    dateStyle: 'short',
    timeStyle: 'short',
    ...options,
  })
}

export const formatCurrency = (
  value: number,
  options: Record<string, unknown> = {},
) => {
  const { $i18n } = useNuxtApp()
  const currency = useCurrencyStore()

  // Some API paths still hand over numeric strings; "12" is a whole amount too.
  const amount = Number(value)
  const currencyCode = currency.code

  if (!currencyCode) return value.toString()

  const formatter = new Intl.NumberFormat(getLocale($i18n.locale.value), {
    style: 'currency',
    currency: currencyCode,
    // Whole amounts stay "US$554"; any cents show both digits ("US$581.70",
    // not "US$581.7") — Ticket Protection (BOAT-1977) puts cents on totals.
    minimumFractionDigits: Number.isInteger(amount) ? 0 : 2,
    ...options,
  })

  return formatter.format(amount)
}

/** Round per-night quotes without changing exact cart, order or gift-card amounts. */
export const formatQuotedCurrency = (
  value: number,
  options: Record<string, unknown> = {},
) => {
  const currency = useCurrencyStore()
  const code = normalizeCurrencyCode(options.currency ?? currency.code)
  const siteCurrency = useSiteStore().data?.currency
  const roundUp = currency.list?.find((option) => option.code === code)?.roundUp
    ?? (normalizeCurrencyCode(siteCurrency?.code) === code ? siteCurrency?.roundUp : undefined)
    ?? 1

  return formatCurrency(roundQuotedPrice(value, roundUp), options)
}

/**
 * A whole-number percentage with a leading minus for drops (e.g. -18%). The API
 * already rounds `discount_percent`, so we only guard the sign and trim to an int.
 */
export const formatPercent = (value: number): string =>
  `-${Math.abs(Math.round(value))}%`

export const capitalize = (string: string): string =>
  string.charAt(0).toUpperCase() + string.slice(1)

export const uniqueArray = <T>(array: T[]): T[] => [...new Set(array)]

export const cleanPath = (...paths: string[]): string => {
  const path = `${paths.join('/')}`.replace(/\/+/g, '/').trim()

  if (path === '/') return path

  return path.replace(/\/$/, '')
}

export const getCanonicalLink = (path: string): string => {
  const { domain } = useSite().site.value

  const url = `${domain}/${path}/`

  let cleanedUrl = cleanPath(url)

  if (cleanedUrl.endsWith('/')) {
    cleanedUrl = cleanedUrl.slice(0, -1)
  }

  return `https://${cleanedUrl}`
}
